import React, { forwardRef } from 'react';
import styles from './Input.module.css';

/**
 * Input Component
 *
 * A reusable input component with label, validation states, and icons
 * following the AB Civil design system.
 *
 * @param {Object} props
 * @param {string} props.type - Input type: 'text' | 'number' | 'email' | 'password' | 'date' | 'tel' | 'url'
 * @param {string} props.label - Input label
 * @param {string} props.placeholder - Placeholder text
 * @param {string|number} props.value - Input value
 * @param {function} props.onChange - Change handler
 * @param {string} props.error - Error message
 * @param {string} props.helperText - Helper text
 * @param {boolean} props.disabled - Disable input
 * @param {boolean} props.required - Mark as required
 * @param {React.ReactNode} props.icon - Icon element
 * @param {string} props.iconPosition - Icon position: 'left' | 'right'
 * @param {string} props.size - Input size: 'sm' | 'md' | 'lg'
 * @param {string} props.className - Additional CSS classes
 */
const Input = forwardRef(
  (
    {
      type = 'text',
      label = null,
      placeholder = '',
      value,
      onChange,
      error = null,
      helperText = null,
      disabled = false,
      required = false,
      icon = null,
      iconPosition = 'left',
      size = 'md',
      className = '',
      ...rest
    },
    ref
  ) => {
    const hasError = !!error;
    const showHelperText = error || helperText;

    const containerClasses = [
      styles.container,
      hasError && styles.hasError,
      disabled && styles.isDisabled,
      className,
    ]
      .filter(Boolean)
      .join(' ');

    const inputWrapperClasses = [
      styles.inputWrapper,
      styles[size],
      hasError && styles.error,
      icon && styles.hasIcon,
      icon && iconPosition === 'left' && styles.iconLeft,
      icon && iconPosition === 'right' && styles.iconRight,
    ]
      .filter(Boolean)
      .join(' ');

    return (
      <div className={containerClasses}>
        {label && (
          <label className={styles.label}>
            {label}
            {required && <span className={styles.required}>*</span>}
          </label>
        )}

        <div className={inputWrapperClasses}>
          {icon && iconPosition === 'left' && (
            <span className={styles.iconContainer}>{icon}</span>
          )}

          <input
            ref={ref}
            type={type}
            className={styles.input}
            placeholder={placeholder}
            value={value}
            onChange={onChange}
            disabled={disabled}
            required={required}
            onWheel={type === 'number' ? (e) => e.target.blur() : undefined}
            {...rest}
          />

          {icon && iconPosition === 'right' && (
            <span className={styles.iconContainer}>{icon}</span>
          )}
        </div>

        {showHelperText && (
          <p className={hasError ? styles.errorText : styles.helperText}>
            {error || helperText}
          </p>
        )}
      </div>
    );
  }
);

Input.displayName = 'Input';

export default Input;
